//** Import Components */
import { useState } from "react";
import axiosWithAuth from "../utils/axiosWithAuth";
import {
  Form,
  Input,
  Modal,
  Button,
  message,
  Table,
  Tooltip,
  Empty,
  Space,
  Collapse,
  Grid,
  Pagination,
} from "antd";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { TABLE_DATE_FORMAT } from "../common/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faEdit } from "@fortawesome/free-solid-svg-icons";

export const useManagementBusinessType = ({
  itemType,
  type,
  creatorID,
  user,
  modalData,
  dispatchAction,
  deleteItemAPI,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [modalForm] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { Panel } = Collapse;

  //* Function to create new item
  const createListItem = (values) => {
    const APIURL = `/api/business/${itemType}`;

    const listingData = {
      data: {
        name: values.name,
      },
    };

    axiosWithAuth()
      .post(APIURL, listingData)
      .then((res) => {
        console.log(res.data);

        let config = {
          content: `New ${itemType} created!`,
          style: {
            background: "none",
          },
        };
        message.success(config);

        closeModal();
        setIsSubmitting(false);
        dispatch(dispatchAction);
        modalForm.resetFields();
      })
      .catch((err) => {
        console.log(`Error creating ${itemType}:`, err.response);
        message.error(err.message);
        setIsSubmitting(false);
      });
  };

  //* Function to delete a list item
  const deleteListItem = (APIURL) => {
    if (window.confirm("Are you sure you wish to delete this item?")) {
      axiosWithAuth()
        .delete(APIURL)
        .then((res) => {
          dispatch(dispatchAction);
          console.log(res.data, APIURL);
        })
        .catch((err) => {
          console.log(`Error deleting ${itemType}:`, err);
        });
    }
  };

  //* functions to handle modal
  const showModal = () => {
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const submitModalForm = () => {
    setIsSubmitting(true);

    modalForm.submit();
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setIsSubmitting(false);
  };

  //* Component that holds the modal
  const modalComponent = (
    <Modal
      open={openModal}
      title={modalData.title}
      onCancel={closeModal}
      footer={[
        <Button key="back" onClick={closeModal}>
          Close
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          onClick={submitModalForm}
        >
          Submit
        </Button>,
      ]}
    >
      <Form
        form={modalForm}
        layout="vertical"
        name="userForm"
        onFinish={createListItem}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          name="name"
          label={modalData.fieldLabel}
          rules={[
            {
              required: true,
              message: "Please input a name!",
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );

  const CreateItemButton = ({ btnText }) => {
    return (
      <div className="create-item">
        <Button type="primary" onClick={showModal}>
          {btnText}
        </Button>
      </div>
    );
  };

  const dateFormat = TABLE_DATE_FORMAT;

  const columns = [
    {
      title: "Name",
      key: "ad_name",
      render: (text) => <Link to={`${text.id}`}>{text.name}</Link>,
    },
    {
      title: "Date Created",
      key: "created",
      render: (text) => (
        <>{moment(new Date(text && text.created)).format(dateFormat)}</>
      ),
    },
    // {
    //   title: "Last Modified",
    //   key: "modified",
    //   render: (text) => (
    //     <>{moment(new Date(text && text.modified)).format(dateFormat)}</>
    //   ),
    // },
    {
      title: "Action",
      key: "action",
      render: (text) => (
        <Space size="middle">
          <button
            onClick={() => {
              navigate(`${text.id}`);
            }}
          >
            <Tooltip title="Edit">
              <FontAwesomeIcon icon={faEdit} />
            </Tooltip>
          </button>
          <button
            onClick={() => {
              const APIURL = `${deleteItemAPI}/${text.id}`;
              deleteListItem(APIURL, text.id);
            }}
          >
            <Tooltip title="Delete">
              <FontAwesomeIcon icon={faTrashAlt} />
            </Tooltip>
          </button>
        </Space>
      ),
    },
  ];

  const CardListComponent = ({ listItems }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const start = (currentPage - 1) * pageSize;
    const currentItems = listItems.slice(start, start + pageSize);

    return (
      <>
        <Collapse accordion>
          {currentItems.map((item) => (
            <Panel
              key={item.id}
              header={
                <div>
                  <strong>{item.name}</strong>
                </div>
              }
            >
              <p>
                <strong>Name:</strong> {item.name}
              </p>
              <p>
                <strong>Date Created:</strong>{" "}
                {moment(new Date(item && item.created)).format(dateFormat)}
              </p>
              <Space size="middle">
                <Button
                  onClick={() => {
                    navigate(`${item.id}`);
                  }}
                >
                  <Tooltip title="Edit">
                    <FontAwesomeIcon icon={faEdit} />
                  </Tooltip>
                </Button>
                <Button
                  onClick={() => {
                    const APIURL = `${deleteItemAPI}/${item.id}`;
                    deleteListItem(APIURL, item.id);
                  }}
                >
                  <Tooltip title="Delete">
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Tooltip>
                </Button>
              </Space>
            </Panel>
          ))}
        </Collapse>

        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={listItems.length}
            onChange={(page) => setCurrentPage(page)}
            size="small"
          />
        </div>
      </>
    );
  };

  const ListComponent = ({ emptyMessage, listItems }) => {
    if (listItems.length === 0) {
      return (
        <Empty
          image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
          imageStyle={{ height: 60 }}
          description={<span>{emptyMessage}</span>}
        />
      );
    }

    return screens.md ? (
      <Table columns={columns} dataSource={listItems} />
    ) : (
      <CardListComponent listItems={listItems} />
    );
  };

  return [modalComponent, CreateItemButton, ListComponent];
};
